import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withLocalize } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import {
    eventShape,
    ParameterTypes,
    ParameterDisplayTypes,
    partnerShape,
    SupportTypes,
    templateShape,
    PostbackWindowStartingPoint,
    organizationOptionsShape,
} from '../../types';
import { SmallDialog, Table } from '../../../../components/widgets';
import TableActions from '../../../../components/widgets/TableActions';
import tableActionsCss from '../../../../components/widgets/TableActions.css';
import shelfCss from '../PartnerConfigurationShelf.css';
import {
    isDisabled,
    getSendAllEventsTooltip,
    orgSupportAdvancedPostbacksFeatures,
    getUsedParameters,
} from '../utils';
import {
    getAutocompleteCellProps,
    getCheckboxCellProps,
    getDropdownCellProps,
    getTableCellProps,
    getTextCellProps,
    getWindowCell,
    getSendAllCellValues,
} from '../tableCellsUtils';
import AdvancedPostbackSettingsFactory from '../advancedPostbackSettings/AdvancedPostbackSettingsFactory';
import advancedPostbackCss from '../advancedPostbackSettings/AdvancedPostbackSettings.css';
import { getIsAgency } from '../../../../selectors/user';
import { POSTBACK_FILTERS_TYPES } from '../advancedPostbackSettings/PostbackFilters';

const getTranslation = key => `STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.EVENT_POSTBACKS_TABLE.${key}`;

export const getDropdownOption = (displayName, name) => {
    if (!displayName && !name) return undefined;

    return { display_name: displayName, name: name || displayName };
};

export const getAutocompleteOption = (displayName, name) => {
    if (!displayName && !name) return undefined;

    return { label: displayName, value: name || displayName };
};

export const getDefaultEventPostback = (partner, template, parameters, postbackExtraFields) => ({
    eventId: null,
    sendAll: [SupportTypes.MANDATORY, SupportTypes.RECOMMENDED].includes(partner.sendAllEvents),
    postbackWindowStartingPoint:
        partner.sendAllEvents === SupportTypes.MANDATORY
            ? PostbackWindowStartingPoint.UNLIMITED
            : PostbackWindowStartingPoint.INSTALL,
    postbackWindow: partner.sendAllEvents !== SupportTypes.MANDATORY ? template.installWindow : '',
    ...postbackExtraFields,
    ...parameters.reduce((total, param) => {
        if (param.displayType === ParameterDisplayTypes.CHECKBOX) {
            // Set default value for checkbox parameters based on predefinedValues
            const defaultValue = param.predefinedValues?.[0] === 'True' ? 'True' : 'False';
            return { ...total, [param.name]: defaultValue };
        }
        return { ...total, [param.name]: '' };
    }, {}),
});

function EventPostbacksTable({
    eventsPostbacks = null,
    partner,
    template,
    events = [],
    allowedEvents = null,
    extraTableCells = [],
    postbackExtraFields = {},
    setPostbacks = () => {},
    setTouched = () => {},
    errors = [],
    touched = [],
    tableDataTestId = null,
    isReadonly = false,
    orgOptions,
    translate,
    availableFilters,
}) {
    // events = events to show (may include disallowed events for display)
    // allowedEvents = events that can be selected in dropdown (only allowed ones, filtered same as events)
    // For dropdown options, use events but filter out _disallowed ones
    const selectableEvents = events.filter(e => !e._disallowed);
    const [editedPostbackIndex, setEditedPostbackIndex] = useState(null);
    const { platform, type: templateType } = template;
    const isAgency = useSelector(state => getIsAgency(state));
    const advancedPostbackSupport = orgSupportAdvancedPostbacksFeatures(orgOptions);
    const parameters = getUsedParameters(partner, platform, templateType, [ParameterTypes.POSTBACK]);
    const touchedPostbacks = !touched?.length ? Array(eventsPostbacks?.length || 1).fill({}) : touched;
    const postbacks = !eventsPostbacks?.length
        ? [getDefaultEventPostback(partner, template, parameters, postbackExtraFields)]
        : eventsPostbacks.map(postback => {
              // Check if this event is disallowed (marked with _disallowed flag)
              const event = events.find(e => e.id === postback.eventId);
              const isDisallowedEvent = event?._disallowed || false;
              return { ...postback, disabled: isReadonly || isDisallowedEvent };
          });

    const parameterWithPredefinedValues =
        parameters.length > 0 ? parameters.filter(parameter => parameter.predefinedValues?.length > 0) : [];
    const predefinedOptionsParameter =
        parameterWithPredefinedValues.length === 1 ? parameterWithPredefinedValues[0] : null;

    const usedEvents = predefinedOptionsParameter
        ? postbacks.map(row => `${row.eventId} ${row[predefinedOptionsParameter.name]}`).filter(Boolean)
        : postbacks.map(row => row.eventId).filter(Boolean);

    // Use selectableEvents (allowedEvents) for remaining events in dropdown
    // This ensures only allowed events can be selected, not disallowed ones
    const remainingEvents = predefinedOptionsParameter
        ? selectableEvents.filter(
              event =>
                  usedEvents.filter(eventParam => eventParam.startsWith(event.id)).length !==
                  predefinedOptionsParameter.predefinedValues.length
          )
        : selectableEvents.filter(event => !usedEvents.some(eventId => eventId === event.id));

    const onAddRowClicked = () => {
        setPostbacks([...postbacks, getDefaultEventPostback(partner, template, parameters, postbackExtraFields)]);
        setTouched([...touchedPostbacks, {}]);
    };

    const clearEditedPostback = () => setEditedPostbackIndex(null);

    const onDeleteRowClicked = rowIndex => {
        const updatedPostbacks = [...postbacks];
        updatedPostbacks.splice(rowIndex, 1);
        updatedPostbacks.length === 0 ? setPostbacks(null) : setPostbacks(updatedPostbacks);

        const updatedTouchedPostbacks = [...touchedPostbacks];
        updatedTouchedPostbacks.splice(rowIndex, 1);
        updatedTouchedPostbacks.length === 0 ? setTouched(null) : setTouched(updatedTouchedPostbacks);
    };

    const getErrorCellProp = field => ({
        propName: 'error',
        transformation: (_, index) => touchedPostbacks?.[index]?.[field] && errors?.[index]?.[field],
    });

    const getValueCellProp = (field, transformation) => ({
        propName: 'value',
        transformation: (_, index) => transformation(postbacks[index]?.[field]),
    });

    const getRowRemainingEvents = currentRowIndex => {
        if (!predefinedOptionsParameter || currentRowIndex >= postbacks?.length) {
            return remainingEvents;
        }

        // if we have postback paramerter with multiple options we're allowing mapping same sdk event to different options,
        // but not duplicate them
        return remainingEvents.filter(
            remainingEvent =>
                !postbacks.some(
                    (postback, postbackIndex) =>
                        postbackIndex !== currentRowIndex &&
                        postback?.eventId === remainingEvent.id &&
                        postback[predefinedOptionsParameter.name] !== null &&
                        postback[predefinedOptionsParameter.name] ===
                            postbacks[currentRowIndex][predefinedOptionsParameter.name]
                )
        );
    };

    const getRemainingPredefinedValues = (currentRowIndex, predefinedValues, paramName) => {
        if (currentRowIndex >= postbacks?.length) {
            return predefinedValues;
        }

        // if we have postback paramerter with multiple options we allowing mapping same sdk event to different options,
        // but not duplicate them
        return predefinedValues.filter(
            value =>
                !postbacks.some(
                    (postback, postbackIndex) =>
                        postbackIndex !== currentRowIndex &&
                        postback?.eventId === postbacks[currentRowIndex]?.eventId &&
                        postback[paramName] === value
                )
        );
    };

    const onFieldUpdated = (value, field, index) => {
        const updatedTouched = [...touchedPostbacks];
        updatedTouched[index][field] = true;
        setTouched(updatedTouched);

        const updatedPostbacks = [...postbacks];
        updatedPostbacks[index][field] = value;

        if (field === 'sendAll') {
            updatedPostbacks[index].postbackWindowStartingPoint = PostbackWindowStartingPoint.UNLIMITED;
            updatedPostbacks[index].postbackWindow = '';
        }

        // keep event name updated if eventId is updated
        if (field === 'eventId') {
            updatedPostbacks[index].eventName = events.find(event => event.id === value)?.eventName;
        }

        // postback checkbox param should be saved as string type (like all pb params!)
        const paramField = parameters.filter(param => param.name === field);
        if (paramField.length > 0 && paramField[0].displayType === ParameterDisplayTypes.CHECKBOX) {
            updatedPostbacks[index][field] = value ? 'True' : 'False';
        }

        setPostbacks(updatedPostbacks);
    };

    const updateAdvancedPostbackFields = (campaignRegex, customByUser, postbackUri, filters) => {
        onFieldUpdated(campaignRegex, 'campaignRegex', editedPostbackIndex);
        onFieldUpdated(customByUser, 'customByUser', editedPostbackIndex);
        onFieldUpdated(customByUser ? postbackUri : null, 'postbackUri', editedPostbackIndex);
        onFieldUpdated(filters, 'filters', editedPostbackIndex);
        setEditedPostbackIndex(null);
    };

    const eventCell = getAutocompleteCellProps(
        'eventId',
        getTranslation('SDK_EVENT_NAME'),
        getTranslation('SDK_EVENT_NAME_TOOLTIP'),
        index => getRowRemainingEvents(index).map(event => getAutocompleteOption(event.displayName, event.id)),
        onFieldUpdated,
        [
            getErrorCellProp('eventId'),
            getValueCellProp('eventId', value => {
                // Look in events (includes disallowed) for display
                const event = events.find(({ id }) => value === id);
                if (event) {
                    return getAutocompleteOption(event.displayName, event.id);
                }
                // Fallback: show configured event name even if not in events list
                const configuredEvent = eventsPostbacks?.find(p => p.eventId === value);
                return configuredEvent ? getAutocompleteOption(configuredEvent.eventName, value) : undefined;
            }),
        ]
    );

    const parametersCells = parameters.map(({ name, label, description, predefinedValues, displayType }) => {
        switch (displayType) {
            case ParameterDisplayTypes.CHECKBOX:
                return getCheckboxCellProps(
                    name,
                    label,
                    description,
                    {},
                    isReadonly,
                    { tdClassName: classNames(shelfCss.tableCell, shelfCss.checkboxCell) },
                    onFieldUpdated,
                    [
                        getValueCellProp(name, value => {
                            // Handle string values ('True'/'False') for checkbox parameters
                            return value === 'True';
                        }),
                    ]
                );
            case ParameterDisplayTypes.TEXT:
                return getTextCellProps(name, label, description, {}, [getErrorCellProp(name)]);
            default:
                return getAutocompleteCellProps(
                    name,
                    label,
                    description,
                    index =>
                        getRemainingPredefinedValues(index, predefinedValues, name).map(value =>
                            getAutocompleteOption(value)
                        ),
                    onFieldUpdated,
                    [getErrorCellProp(name), getValueCellProp(name, getAutocompleteOption)]
                );
        }
    });

    const sendAllCell = getCheckboxCellProps(
        'sendAll',
        'STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.SEND_ALL_EVENTS',
        getTranslation('SEND_ALL_EVENTS_TOOLTIP'),
        { partnerName: partner.displayName },
        isDisabled(partner.sendAllEvents, isReadonly || isAgency),
        {
            tdClassName: classNames(shelfCss.tableCell, shelfCss.checkboxCell),
            tooltipText: getSendAllEventsTooltip(partner.sendAllEvents, isReadonly),
            tooltipData: { partnerName: partner.displayName },
        },
        null,
        getSendAllCellValues(index => postbacks[index])
    );

    const postbackWindowStartPointCell = getDropdownCellProps(
        'postbackWindowStartingPoint',
        getTranslation('WINDOW_STARTING_POINT'),
        getTranslation('WINDOW_STARTING_POINT_TOOLTIP'),
        Object.values(PostbackWindowStartingPoint).map(value => getDropdownOption(value)),
        onFieldUpdated,
        [
            getErrorCellProp('postbackWindowStartingPoint'),
            getValueCellProp('postbackWindowStartingPoint', getDropdownOption),
            { propName: 'disabled', dataKey: 'sendAll', transformation: sendAll => sendAll || isReadonly },
        ]
    );

    const postbackWindowCell = getWindowCell(
        'postbackWindow',
        getTranslation('POSTBACK_WINDOW'),
        getTranslation('POSTBACK_WINDOW_TOOLTIP'),
        { partnerName: partner.displayName },
        [
            getErrorCellProp('postbackWindow'),
            {
                propName: 'disabled',
                dataKey: 'postbackWindowStartingPoint',
                transformation: startPoint => startPoint === PostbackWindowStartingPoint.UNLIMITED || isReadonly,
            },
        ]
    );

    const tableActionsCell = {
        ...getTableCellProps(null, ''),
        cellComponent: TableActions,
        cellProps: {
            onAdd: isReadonly ? null : onAddRowClicked,
            onDelete: isReadonly ? null : onDeleteRowClicked,
            onEdit: advancedPostbackSupport ? i => setEditedPostbackIndex(i) : null,
            addClassName: i =>
                classNames({
                    [tableActionsCss.hidden]: i !== postbacks.length - 1,
                    [tableActionsCss.disabled]: !remainingEvents.length || !postbacks[i]?.eventId,
                }),
            editClassName: i =>
                classNames({
                    [tableActionsCss.hidden]: !postbacks[i]?.eventId,
                    [tableActionsCss.dot]:
                        postbacks[i]?.customByUser || postbacks[i]?.filters?.length || postbacks[i]?.campaignRegex,
                }),
            editTooltip: i => {
                const postback = postbacks[i];
                return postback?.customByUser || postback?.filters?.length || postback?.campaignRegex
                    ? translate('STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.CUSTOM_POSTBACK_TOOLTIP_APPLIED')
                    : translate('STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.CUSTOM_POSTBACK_TOOLTIP');
            },
            addTooltip: i => {
                if (!postbacks[i]?.eventId) return getTranslation('EVENT_NOT_FILLED');
                else if (!remainingEvents.length) return getTranslation('NO_REMAINING_EVENTS');
                else return null;
            },
        },
    };

    const tableMetadata = [
        eventCell,
        ...parametersCells,
        sendAllCell,
        postbackWindowStartPointCell,
        postbackWindowCell,
        ...extraTableCells,
        ...(isReadonly && !advancedPostbackSupport ? [] : [tableActionsCell]),
    ];

    const editedPostback = editedPostbackIndex !== null ? postbacks[editedPostbackIndex] : null;

    return (
        <>
            <Table
                dataTestId={tableDataTestId}
                data={postbacks}
                metadata={tableMetadata}
                trClass={shelfCss.tableRow}
                zebraTable={false}
                rowHoverEnabled={false}
                onToggleSingleRow={(value, row, field, index) => onFieldUpdated(value, field, index)}
            />
            <SmallDialog
                open={!!editedPostback}
                onClose={clearEditedPostback}
                onEscapePress={clearEditedPostback}
                classes={{
                    paperScrollBody: classNames(
                        advancedPostbackCss.postbackPopup,
                        availableFilters?.length > 0 && advancedPostbackCss.postbackPopupWithFilters
                    ),
                }}
            >
                {editedPostback && (
                    <AdvancedPostbackSettingsFactory
                        template={template}
                        orgOptions={orgOptions}
                        partner={partner}
                        isReadonly={isReadonly}
                        postback={editedPostback}
                        onReject={clearEditedPostback}
                        eventName={events.find(event => event.id === editedPostback.eventId)?.displayName}
                        onAccept={updateAdvancedPostbackFields}
                        availableFilters={availableFilters}
                    />
                )}
            </SmallDialog>
        </>
    );
}

EventPostbacksTable.propTypes = {
    partner: partnerShape.isRequired,
    orgOptions: organizationOptionsShape.isRequired,
    template: templateShape.isRequired,
    events: PropTypes.arrayOf(eventShape),
    allowedEvents: PropTypes.arrayOf(eventShape),
    extraTableCells: PropTypes.arrayOf(PropTypes.object),
    postbackExtraFields: PropTypes.object,
    eventsPostbacks: PropTypes.arrayOf(PropTypes.object),
    tableDataTestId: PropTypes.string,
    setPostbacks: PropTypes.func,
    setTouched: PropTypes.func,
    errors: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object)]),
    touched: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object)]),
    isReadonly: PropTypes.bool,
    translate: PropTypes.func.isRequired,
    availableFilters: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            displayName: PropTypes.string,
            type: PropTypes.oneOf(Object.values(POSTBACK_FILTERS_TYPES)),
        })
    ),
};

export default withLocalize(EventPostbacksTable);
